import { useState } from 'react'
import { useTranslation } from 'react-i18next'

// Icon base URL from Supabase
const ICON_BASE_URL = 'https://ozhkjwcjsifdhfdexayd.supabase.co/storage/v1/object/public/website-images/icons2'

function About() {
  const { t } = useTranslation()
  const [openFaqId, setOpenFaqId] = useState<number | null>(null)

  const teamValues = [
    {
      id: 1,
      title: t('about.values.value1.title'),
      description: t('about.values.value1.description'),
      icon: `${ICON_BASE_URL}/clock-front-color.png`,
      color: 'baby-blue'
    },
    {
      id: 2,
      title: t('about.values.value2.title'),
      description: t('about.values.value2.description'),
      icon: `${ICON_BASE_URL}/chat-bubble-front-color.png`,
      color: 'pink'
    },
    {
      id: 3,
      title: t('about.values.value3.title'),
      description: t('about.values.value3.description'),
      icon: `${ICON_BASE_URL}/dollar-front-color.png`,
      color: 'baby-blue'
    },
    {
      id: 4,
      title: t('about.values.value4.title'),
      description: t('about.values.value4.description'),
      icon: `${ICON_BASE_URL}/sheild-front-color.png`,
      color: 'pink'
    }
  ]

  const faqs = [
    {
      id: 1,
      question: t('about.faq.q1.question'),
      answer: t('about.faq.q1.answer')
    },
    {
      id: 2,
      question: t('about.faq.q2.question'),
      answer: t('about.faq.q2.answer')
    },
    {
      id: 3,
      question: t('about.faq.q3.question'),
      answer: t('about.faq.q3.answer')
    },
    {
      id: 4,
      question: t('about.faq.q4.question'),
      answer: t('about.faq.q4.answer')
    },
    {
      id: 5,
      question: t('about.faq.q5.question'),
      answer: t('about.faq.q5.answer')
    },
    {
      id: 6,
      question: t('about.faq.q6.question'),
      answer: t('about.faq.q6.answer')
    },
    {
      id: 7,
      question: t('about.faq.q7.question'),
      answer: t('about.faq.q7.answer')
    },
    {
      id: 8,
      question: t('about.faq.q8.question'),
      answer: t('about.faq.q8.answer')
    }
  ]

  const toggleFaq = (id: number) => {
    setOpenFaqId(openFaqId === id ? null : id)
  }

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-baby-blue-200 via-baby-blue-50 to-pink-200 px-4 py-16 md:py-20 lg:py-28 min-h-[300px] md:min-h-[350px] overflow-hidden">
        {/* Grid Pattern Background */}
        <div
          className="absolute inset-0 bg-grid-light"
          aria-hidden="true"
        />

        <div className="relative max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
            {t('about.hero.title1')}{' '}
            <span className="text-baby-blue-600">{t('about.hero.digital')}</span>{' '}
            <span className="text-pink-500">{t('about.hero.amigos')}</span>
          </h1>

          <p className="text-base md:text-lg lg:text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
            {t('about.hero.subtitle')}
          </p>

          <div className="flex justify-center">
            <img
              src={`${ICON_BASE_URL}/megaphone-front-color.png`}
              alt="Digital Amigos Team"
              className="w-20 h-20 md:w-24 md:h-24"
            />
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="bg-white px-4 py-12 md:py-16 lg:py-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center mb-16 md:mb-20">
            {/* Image Side */}
            <div className="relative order-2 lg:order-1">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=800"
                  alt="Team collaboration and teamwork"
                  crossOrigin="anonymous"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-baby-blue-600/20 to-pink-500/20" />
              </div>

              {/* Floating Badge */}
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-4 md:p-6 border-2 border-baby-blue-500">
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-baby-blue-600">500+</div>
                  <div className="text-sm text-gray-600">Happy Clients</div>
                </div>
              </div>
            </div>

            {/* Content Side */}
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                {t('about.story.title')}
              </h2>

              <div className="space-y-4 text-base md:text-lg text-gray-700 leading-relaxed">
                <p>
                  {t('about.story.p1')}
                </p>

                <p>
                  {t('about.story.p2')}
                </p>

                <p>
                  {t('about.story.p3')}
                </p>

                <p className="font-semibold text-baby-blue-600">
                  {t('about.story.p4')}
                </p>
              </div>
            </div>
          </div>

          {/* Our Values */}
          <div className="mt-12 md:mt-16">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                {t('about.values.title')}
              </h2>
              <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
                {t('about.values.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {teamValues.map((value) => (
                <div
                  key={value.id}
                  className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
                >
                  {/* Icon */}
                  <div className={`w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-${value.color}-100 flex items-center justify-center mb-6 mx-auto`}>
                    <img
                      src={value.icon}
                      alt={value.title}
                      className="w-10 h-10 md:w-12 md:h-12"
                    />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 text-center">
                    {value.title}
                  </h3>
                  <p className="text-base text-gray-600 text-center leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Mission Statement */}
          <div className="mt-16 md:mt-20 bg-gradient-to-r from-baby-blue-600 to-baby-blue-700 rounded-2xl p-8 md:p-12 text-center shadow-2xl">
            <img
              src={`${ICON_BASE_URL}/star-front-color.png`}
              alt="Mission"
              className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-6"
            />
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4">
              {t('about.mission.title')}
            </h3>
            <p className="text-base md:text-lg lg:text-xl text-baby-blue-100 max-w-4xl mx-auto leading-relaxed">
              {t('about.mission.description')}
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-gradient-to-br from-baby-blue-50 to-pink-50 px-4 py-12 md:py-16 lg:py-24">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('about.faq.title')}
            </h2>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              {t('about.faq.subtitle')}
            </p>
          </div>

          {/* FAQ Accordion */}
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div
                key={faq.id}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-200"
              >
                <button
                  onClick={() => toggleFaq(faq.id)}
                  className="w-full px-6 py-5 md:px-8 md:py-6 flex items-center justify-between gap-4 text-left hover:bg-gray-50 transition-colors duration-200"
                  aria-expanded={openFaqId === faq.id}
                >
                  <span className="text-base md:text-lg font-semibold text-gray-900 leading-snug pr-4">
                    {faq.question}
                  </span>

                  <div className="flex-shrink-0">
                    {openFaqId === faq.id ? (
                      <svg
                        className="w-6 h-6 text-baby-blue-600 transition-transform duration-200"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    ) : (
                      <svg
                        className="w-6 h-6 text-gray-400 transition-transform duration-200"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </div>
                </button>

                {openFaqId === faq.id && (
                  <div className="px-6 pb-5 md:px-8 md:pb-6 pt-2">
                    <p className="text-base text-gray-700 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="mt-12 md:mt-16 text-center bg-white rounded-2xl p-8 md:p-10 shadow-xl border-2 border-baby-blue-200">
            <img
              src={`${ICON_BASE_URL}/phone-income-front-color.png`}
              alt="Contact us"
              className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-6"
            />
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              {t('about.faq.contactTitle')}
            </h3>
            <p className="text-base md:text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
              {t('about.faq.contactSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="w-full sm:w-auto h-12 md:h-14 px-8 md:px-10 bg-baby-blue-600 text-white font-semibold rounded-lg hover:bg-baby-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center text-base md:text-lg"
              >
                {t('about.faq.contactButton')}
              </a>
              <a
                href="tel:+13615106451"
                className="w-full sm:w-auto h-12 md:h-14 px-8 md:px-10 bg-white text-baby-blue-600 font-semibold rounded-lg hover:bg-gray-50 transition-all duration-200 border-2 border-baby-blue-600 flex items-center justify-center text-base md:text-lg"
              >
                {t('about.faq.callButton')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative bg-gradient-to-r from-pink-500 to-pink-600 px-4 py-12 md:py-16 lg:py-20 overflow-hidden">
        {/* Grid Pattern - subtle on colored background */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(to right, rgba(255, 255, 255, 0.08) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(255, 255, 255, 0.08) 1px, transparent 1px)
            `,
            backgroundSize: '32px 32px',
          }}
          aria-hidden="true"
        />

        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">
            {t('about.cta.title')}
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-pink-100 mb-8">
            {t('about.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="w-full sm:w-auto h-12 md:h-14 px-8 md:px-10 bg-white text-pink-600 font-semibold rounded-lg hover:bg-gray-50 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center text-base md:text-lg"
            >
              {t('about.cta.button1')}
            </a>
            <a
              href="/services"
              className="w-full sm:w-auto h-12 md:h-14 px-8 md:px-10 bg-transparent text-white font-semibold rounded-lg hover:bg-pink-700 transition-all duration-200 border-2 border-white flex items-center justify-center text-base md:text-lg"
            >
              {t('about.cta.button2')}
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default About
